/**
 * ConnectionHealthBanner
 * Warning banner shown when a bank connection is stale (>48h) or broken.
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

interface ConnectionData {
  id: string | number;
  status: string;
  last_synced_at?: string | null;
  bank_name?: string;
}

interface Props {
  connection: ConnectionData;
  onReconnect: () => void;
}

const BROKEN_STATUSES = ['error', 'sca_needed', 'webauth_needed', 'expired'];
const STALE_THRESHOLD_MS = 48 * 60 * 60 * 1000; // 48 hours

export function isConnectionUnhealthy(connection: ConnectionData): boolean {
  if (BROKEN_STATUSES.includes(connection.status)) return true;
  if (connection.status === 'connected' && connection.last_synced_at) {
    const lastSync = new Date(connection.last_synced_at).getTime();
    return Date.now() - lastSync > STALE_THRESHOLD_MS;
  }
  return false;
}

export function getStaleLabel(connection: ConnectionData): { label: string; color: string } | null {
  if (BROKEN_STATUSES.includes(connection.status)) {
    const daysSince = connection.last_synced_at
      ? Math.round((Date.now() - new Date(connection.last_synced_at).getTime()) / (24 * 60 * 60 * 1000))
      : null;
    const suffix = daysSince ? ` · il y a ${daysSince}j` : '';
    return { label: `Déconnecté${suffix}`, color: '#EF4444' };
  }

  if (connection.status === 'connected' && connection.last_synced_at) {
    const lastSync = new Date(connection.last_synced_at).getTime();
    const msSince = Date.now() - lastSync;
    if (msSince > STALE_THRESHOLD_MS) {
      const daysSince = Math.round(msSince / (24 * 60 * 60 * 1000));
      return { label: `Sync en retard · il y a ${daysSince}j`, color: '#F59E0B' };
    }
  }

  return null;
}

export default function ConnectionHealthBanner({ connection, onReconnect }: Props) {
  const isBroken = BROKEN_STATUSES.includes(connection.status);
  const bankName = connection.bank_name || 'ta banque';
  const borderColor = isBroken ? '#EF4444' : '#F59E0B';
  const bgColor = isBroken ? 'rgba(239, 68, 68, 0.06)' : 'rgba(245, 158, 11, 0.06)';
  const iconColor = isBroken ? '#EF4444' : '#F59E0B';
  const iconName = isBroken ? 'alert-circle' : 'alert-outline';

  const staleDays = connection.last_synced_at
    ? Math.round((Date.now() - new Date(connection.last_synced_at).getTime()) / (24 * 60 * 60 * 1000))
    : null;

  return (
    <View style={[styles.banner, { backgroundColor: bgColor, borderLeftColor: borderColor }]}>
      <View style={styles.content}>
        <Icon name={iconName} size={20} color={iconColor} style={styles.icon} />
        <View style={styles.textContainer}>
          {isBroken ? (
            <Text style={styles.text}>
              <Text style={styles.bold}>{bankName}</Text> nécessite une reconnexion pour continuer la synchronisation.
            </Text>
          ) : (
            <Text style={styles.text}>
              <Text style={styles.bold}>{bankName}</Text> n'a pas synchronisé depuis{' '}
              {staleDays ? `${staleDays} jours` : 'un moment'}.
            </Text>
          )}
        </View>
      </View>

      <TouchableOpacity style={[styles.button, { backgroundColor: borderColor }]} onPress={onReconnect}>
        <Icon name="lock-reset" size={16} color="#FFFFFF" />
        <Text style={styles.buttonText}>Reconnecter {bankName}</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    borderRadius: 12,
    borderLeftWidth: 4,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  icon: {
    marginRight: spacing.sm,
    marginTop: 2,
  },
  textContainer: {
    flex: 1,
  },
  text: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
    lineHeight: 20,
  },
  bold: {
    fontWeight: typography.fontWeight.semibold,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    gap: spacing.xs,
  },
  buttonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: '#FFFFFF',
  },
});
